import React, { useEffect, useState, useRef } from 'react';
import './ParkingField.scss';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { FaTrash, FaPlus, FaExchangeAlt } from 'react-icons/fa';
import parkingAPI from '../../api/parking';
import useModal from '../../customHooks/useModal';
import Modal from '../../components/Modal';
import translations from '../../translations/de';
import Paths from '../../constants/paths';
import Item from '../../components/Item';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Button from '../../components/Button';
import Table from '../../components/Table';
import DatePicker from '../../components/DatePicker';
import ErrorMessages from '../../components/ErrorMessages.js';
import translateLabel from '../../helpers/translateLabel';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import TabSwitcher from '../../components/TabSwitcher';
import Breadcrumb from '../../components/Breadcrumb';
import FieldParkingPasses from './FieldParkingPasses';
import SwitchBatchModal from './SwitchBatchModal';

const ParkingField = ({ store }) => {
    const { field_id } = useParams();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(false);
    const [newDate, setNewDate] = useState('');
    const [fallbackId, setFallbackId] = useState(null);
    const [sperrError, setSperrError] = useState(null);
    // Sperr row whose cards are being batch-switched to/from the fallback lot.
    const [switchSperr, setSwitchSperr] = useState(null);
    // Bumped after a successful add to remount (clear) the date/fallback inputs.
    const [resetKey, setResetKey] = useState(0);
    // Capacity-based "erfassen" (create cards up to capacity for the current season).
    const [fill, setFill] = useState(null);
    const [fillLoading, setFillLoading] = useState(false);
    const [fillError, setFillError] = useState(null);
    const fillModalRef = useRef(null);
    const [fillModal, setFillModal] = useModal(false, fillModalRef);

    const loadFill = async () => {
        try {
            const res = await parkingAPI.seasonFill(field_id);
            setFill(res.data);
        } catch (e) {
            setFill(null);
        }
    };

    const doFill = async () => {
        setFillLoading(true);
        setFillError(null);
        try {
            await parkingAPI.fillField(field_id);
            setFillModal(false);
            await loadFill();
        } catch (err) {
            setFillError(requsetErrorMessage(err && err.message ? err.message : err));
        }
        setFillLoading(false);
    };

    useEffect(() => {
        if (!store.selectedField || String(store.selectedField.id) !== String(field_id)) {
            store.getField(field_id);
        }
    }, [store, field_id]);

    useEffect(() => {
        loadFill();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field_id]);

    const field = store.selectedField;
    if (!field) return null;

    const itemModel = {
        name: field.name,
        capacity: field.capacity,
        issued_target: field.issued_target,
        info_text: field.info_text ?? '',
        latitude: field.latitude ?? '',
        longitude: field.longitude ?? '',
        entrance: field.entrance ?? '',
    };

    const fieldName = (id) => store.fields.find((f) => f.id === id)?.name ?? '-';

    // Empty/garbage -> null so we never store a bogus (0,0) coordinate.
    const toCoord = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const addSperr = () => {
        if (!newDate) {
            setSperrError('Bitte ein Sperrdatum auswählen.');
            return;
        }
        setSperrError(null);
        store
            .addSperrDate({ date: newDate, fallback_parking_field_id: fallbackId })
            .then(() => {
                setNewDate('');
                setFallbackId(null);
                setResetKey((k) => k + 1);
            })
            .catch((err) => setSperrError(requsetErrorMessage(err && err.message ? err.message : err)));
    };

    const sperrColumns = [
        { Header: translations.sperr_dates, accessor: 'date', Cell: ({ value }) => <div>{value}</div> },
        {
            Header: translations.fallback_parking_field_id,
            accessor: 'fallback',
            Cell: ({ value }) => <div>{value}</div>,
        },
        {
            Header: '',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="button-cell">
                    {row.original.hasFallback && (
                        <Button title="Umbuchen" onClick={() => setSwitchSperr(row.original)}>
                            <FaExchangeAlt />
                        </Button>
                    )}
                    <Button
                        title={translations.delete}
                        onClick={() => store.removeSperrDate(row.original.sperrId)}
                    >
                        <FaTrash />
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div style={{ margin: '0 2.5% 15px' }}>
                <Breadcrumb
                    links={[{ path: Paths.PARKING_FIELDS, name: translations.parking_fields }]}
                    name={field.name}
                />
            </div>
            <TabSwitcher initialTabName="Allgemein">
                <TabSwitcher.Tabs>
                    <TabSwitcher.TabPill tabName="Allgemein" />
                    <TabSwitcher.TabPill tabName="Parkkarten" />
                </TabSwitcher.Tabs>
                <TabSwitcher.TabPanel activeWhenTabName="Allgemein">
            <Item
                links={[]}
                name={''}
                itemModel={itemModel}
                edit={edit}
                onEdit={() => setEdit(true)}
                onCancel={() => setEdit(false)}
                error={error}
                loading={loading}
                onSubmit={(event) => {
                    setLoading(true);
                    setError(null);
                    store
                        .editField({
                            name: event.name,
                            capacity: Number(event.capacity),
                            issued_target: Number(event.issued_target),
                            info_text: event.info_text,
                            latitude: toCoord(event.latitude),
                            longitude: toCoord(event.longitude),
                            entrance: event.entrance || null,
                        })
                        .then(() => {
                            setLoading(false);
                            setEdit(false);
                            loadFill();
                        })
                        .catch((err) => {
                            setError(err);
                            setLoading(false);
                        });
                }}
                determineInputType={({ value, name, register, error, disabled, index, control }) => (
                    <Input
                        key={index}
                        control={control}
                        label={translateLabel(name)}
                        name={name}
                        disabled={disabled}
                        type={typeof value === 'number' ? 'number' : 'text'}
                        {...register(name, {
                            valueAsNumber: typeof value === 'number' ? true : false,
                        })}
                        error={error}
                    />
                )}
            >
                <Input
                    label={translations.uses_external_qr}
                    defaultValue={field.uses_external_qr ? 'Ja' : 'Nein'}
                    disabled
                />
                <Input label={translateLabel('slug')} defaultValue={field.slug} disabled />
            </Item>

            {fill && (
                <div style={{ marginTop: '30px' }}>
                    <h3 style={{ margin: '0 2.5% 10px' }}>Parkkarten erfassen</h3>
                    <div
                        className="tab-controls"
                        style={{ alignItems: 'center', margin: '0 2.5%' }}
                    >
                        <div style={{ marginRight: '20px' }}>
                            Erstellt: {fill.created} / {fill.target ?? fill.capacity}
                            {fill.season_title ? ` (Saison: ${fill.season_title})` : ''}
                        </div>
                        <Button
                            label={translations.create}
                            disabled={fill.remaining <= 0}
                            onClick={() => setFillModal(true)}
                        >
                            <FaPlus style={{ marginRight: '5px' }} />
                        </Button>
                    </div>
                    {fill.remaining <= 0 && (
                        <div style={{ margin: '5px 2.5% 0', opacity: 0.7 }}>
                            Ziel erreicht — zum Erfassen die Überbuchung (Ziel) erhöhen.
                        </div>
                    )}
                    <div style={{ margin: '0 2.5%' }}>
                        <ErrorMessages messages={fillError || ''} />
                    </div>
                </div>
            )}

            {fillModal && (
                <Modal
                    title="Parkkarten erfassen"
                    ref={fillModalRef}
                    onClose={() => setFillModal(false)}
                    maxWidth="420px"
                >
                    <p style={{ color: '#333', marginBottom: '20px' }}>
                        {`Sind Sie sicher, dass Sie ${fill ? fill.remaining : 0} Parkkarten erstellen möchten?`}
                    </p>
                    <div className="Modal-Controls">
                        <Button
                            label="Ja"
                            loading={fillLoading}
                            disabled={fillLoading}
                            onClick={doFill}
                            style={{ width: '80px' }}
                        />
                        <Button
                            label="Abbrechen"
                            onClick={() => setFillModal(false)}
                            style={{ width: '120px', marginLeft: '20px' }}
                        />
                    </div>
                </Modal>
            )}

            <div style={{ marginTop: '30px' }}>
                <h3 style={{ margin: '0 2.5% 10px' }}>{translations.sperr_dates}</h3>
                <div className="tab-controls parking-sperr-controls">
                    <DatePicker
                        key={`date-${resetKey}`}
                        label="Sperrdatum"
                        name="sperr_date"
                        selectText="TT.MM.JJJJ"
                        dateOnly
                        onChange={(name, date) => {
                            if (!date) {
                                setNewDate('');
                                return;
                            }
                            const pad = (n) => String(n).padStart(2, '0');
                            setNewDate(
                                `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
                            );
                        }}
                    />
                    <Select
                        key={`fallback-${resetKey}`}
                        label={translations.fallback_parking_field_id}
                        defaultValue={{ label: '-', value: null }}
                        options={[
                            { label: '-', value: null },
                            ...store.fields
                                .filter((f) => f.id !== field.id)
                                .map((f) => ({ label: f.name, value: f.id })),
                        ]}
                        onChange={(e) => setFallbackId(e.value)}
                    />
                    <div className="parking-add-btn-wrap">
                        <label className="parking-add-btn-spacer">&nbsp;</label>
                        <Button label={translations.create} onClick={addSperr}>
                            <FaPlus style={{ marginRight: '5px' }} />
                        </Button>
                    </div>
                </div>
                <div style={{ margin: '0 2.5%' }}>
                    <ErrorMessages messages={sperrError || ''} />
                </div>
                <Table
                    tableName=""
                    columns={sperrColumns}
                    data={field.sperr_dates.map((s) => ({
                        date: s.date,
                        fallback: s.fallback_parking_field_id
                            ? fieldName(s.fallback_parking_field_id)
                            : '-',
                        sperrId: s.id,
                        hasFallback: !!s.fallback_parking_field_id,
                        fallbackName: s.fallback_parking_field_id
                            ? fieldName(s.fallback_parking_field_id)
                            : null,
                    }))}
                />
            </div>
                </TabSwitcher.TabPanel>
                <TabSwitcher.TabPanel activeWhenTabName="Parkkarten">
                    <FieldParkingPasses fieldId={field.id} fieldName={field.name} />
                </TabSwitcher.TabPanel>
            </TabSwitcher>

            {switchSperr && (
                <SwitchBatchModal
                    sperr={switchSperr}
                    blockedFieldName={field.name}
                    fallbackFieldName={switchSperr.fallbackName}
                    onClose={() => setSwitchSperr(null)}
                    onDone={() => {}}
                />
            )}
        </div>
    );
};

export default observer(ParkingField);
